import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface MonthData {
  monat: string;
  soll: number;
  ist: number;
  billable: number;
  nonBillable: number;
}

interface MonthlyComparisonTableProps {
  data: MonthData[];
  /** 0–11 for “(aktuell)” row; -1 when no row is highlighted */
  highlightMonthIndex: number;
  /** Footer totals sum months 0..inclusive (aligned with YTD window) */
  totalsEndMonthInclusive: number;
  year: number;
  selectedMonthForFilter: number | null;
  onMonthClick: (monthIndex: number) => void;
}

export function MonthlyComparisonTable({ data, highlightMonthIndex, totalsEndMonthInclusive, year, selectedMonthForFilter, onMonthClick }: MonthlyComparisonTableProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  const endIdx = Math.min(Math.max(0, totalsEndMonthInclusive), Math.max(0, data.length - 1));
  const totalsRows = data.slice(0, endIdx + 1);
  const totalSoll = totalsRows.reduce((sum, m) => sum + m.soll, 0);
  const totalIst = totalsRows.reduce((sum, m) => sum + m.ist, 0);
  const totalDiff = totalIst - totalSoll;

  return (
    <div className="ov-p-6">
      <div 
        className="ov-flex ov-justify-between ov-items-center ov-mb-4 ov-cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="ov-text-gray-900">
          Monatlicher Vergleich {year}
          {selectedMonthForFilter !== null && (
            <span className="ov-text-green-600 ov-ml-2">(gefiltert: {monthNames[selectedMonthForFilter]})</span>
          )}
        </h3>
        <button className="ov-text-gray-500 ov-hover:text-gray-700 ov-bg-transparent ov-border-none ov-outline-none">
          {isExpanded ? <ChevronUp className="ov-w-5 ov-h-5" /> : <ChevronDown className="ov-w-5 ov-h-5" />}
        </button>
      </div>
      
      {isExpanded && (
      <div className="ov-overflow-x-auto">
        <table className="ov-w-full ov-text-xs ov-h-full">
          <thead className="ov-sticky ov-top-0 ov-bg-white">
            <tr className="ov-border-b ov-border-gray-200">
              <th className="ov-text-left ov-py-4 ov-px-2 ov-text-gray-600">Monat</th>
              <th className="ov-text-right ov-py-4 ov-px-2 ov-text-gray-600">Soll</th>
              <th className="ov-text-right ov-py-4 ov-px-2 ov-text-gray-600">Ist</th>
              <th className="ov-text-right ov-py-4 ov-px-2 ov-text-gray-600">Diff</th>
              <th className="ov-text-right ov-py-4 ov-px-2 ov-text-gray-600">Billable %</th>
            </tr>
          </thead>
          <tbody>
            {data.map((month, index) => {
              const diff = month.ist - month.soll;
              const billablePercent = month.ist > 0 ? (month.billable / month.ist) * 100 : 0;
              const isCurrentMonth = highlightMonthIndex >= 0 && index === highlightMonthIndex;
              const isSelectedForFilter = selectedMonthForFilter === index;
              
              return (
                <tr 
                  key={index} 
                  onClick={() => onMonthClick(index)}
                  className={`ov-border-b ov-border-gray-100 ov-hover:bg-gray-50 ov-cursor-pointer ${
                    isSelectedForFilter ? 'ov-bg-green-50 ov-border-green-200' : isCurrentMonth ? 'ov-bg-blue-50' : ''
                  }`}
                >
                  <td className="ov-py-4 ov-px-2 ov-text-gray-900">
                    {month.monat}
                    {isCurrentMonth && (
                      <span className="ov-ml-2 ov-text-10px ov-text-blue-600">(aktuell)</span>
                    )}
                    {isSelectedForFilter && (
                      <span className="ov-ml-2 ov-text-10px ov-text-green-600">(gefiltert)</span>
                    )}
                  </td>
                  <td className="ov-py-4 ov-px-2 ov-text-right ov-text-gray-700">{month.soll.toFixed(1)}h</td>
                  <td className="ov-py-4 ov-px-2 ov-text-right ov-text-gray-900">{month.ist.toFixed(1)}h</td>
                  <td className={`ov-py-4 ov-px-2 ov-text-right ${
                    diff > 0 ? 'ov-text-green-600' : diff < 0 ? 'ov-text-red-600' : 'ov-text-gray-600'
                  }`}>
                    {diff > 0 ? '+' : ''}{diff.toFixed(1)}h
                  </td>
                  <td className={`ov-py-4 ov-px-2 ov-text-right ${
                    billablePercent >= 85 ? 'ov-text-green-600' : 
                    billablePercent >= 75 ? 'ov-text-yellow-600' : 'ov-text-red-600'
                  }`}>
                    {billablePercent.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="ov-border-t-2 ov-border-gray-300 ov-bg-gray-50">
              <td className="ov-py-4 ov-px-2 ov-text-gray-900">Total</td>
              <td className="ov-py-4 ov-px-2 ov-text-right ov-text-gray-900">
                {totalsRows.reduce((sum, m) => sum + m.soll, 0).toFixed(1)}h
              </td>
              <td className="ov-py-4 ov-px-2 ov-text-right ov-text-gray-900">
                {totalsRows.reduce((sum, m) => sum + m.ist, 0).toFixed(1)}h
              </td>
              <td className={`ov-py-4 ov-px-2 ov-text-right ${
                totalDiff > 0 ? 'ov-text-green-600' : totalDiff < 0 ? 'ov-text-red-600' : 'ov-text-gray-600'
              }`}>
                {totalsRows.reduce((sum, m) => sum + (m.ist - m.soll), 0) > 0 ? '+' : ''}
                {totalsRows.reduce((sum, m) => sum + (m.ist - m.soll), 0).toFixed(1)}h
              </td>
              <td className="ov-py-4 ov-px-2 ov-text-right ov-text-gray-900">
                {totalsRows.reduce((sum, m) => sum + m.ist, 0) > 0 ? (
                  ((totalsRows.reduce((sum, m) => sum + m.billable, 0) /
                  totalsRows.reduce((sum, m) => sum + m.ist, 0)) * 100).toFixed(1)
                ) : '0'}%
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      )}
    </div>
  );
}

